import {
  Component,
  ComponentInterface,
  h,
  EventEmitter,
  Event,
  Host,
  Prop,
  Watch,
} from "@stencil/core";
import { store } from "@stencil/redux";
import {
  CallStateEnum,
  PARAM_CONVERSATION_ORIGIN,
  ConversationOriginEnum,
  KEY_VIDEO_POPOUT,
  ConversationStateEnum,
  IThemeOptions,
  POPOUT_WINDOW_TITLE,
} from "../../models";
import {
  Log,
  AppConfigService as Config,
  WindowEventService,
  DeviceService,
  StorageService,
  ThemeService,
} from "../../services";
import { dispatchCallPoppedOutChange } from "../../store/actions";
import { RootState } from "../../store/states";
import { AVBusService } from "../../services/bus.service";
import { HandledError } from "../../models/HandledError";
import { ErrorCode } from "../../models/enums";
import { ConversationTypeEnum } from "@auvious/integrations";

@Component({
  tag: "app-video-call",
  styleUrl: "app-video-call.scss",
  shadow: true,
})
export class AppVideoCall implements ComponentInterface {
  @Prop() focused: boolean;
  @Prop() open: boolean;
  openChange(open: boolean) {
    // if we have poped out and the conversation ends, close the popout as well
    if (!open && WindowEventService.isTargetPopOutAvailable) {
      WindowEventService.getPopOut().close();
      WindowEventService.setPopOut(undefined);
    }
  }

  @Event() focusChange: EventEmitter<boolean>;
  @Event() popOut: EventEmitter<void>;
  @Event() popIn: EventEmitter<void>;

  @Prop({ mutable: true }) $ticket: string;
  @Watch("$ticket")
  ticketChange(value: string, oldValue: string) {
    // we have poped out a video call and then closed the popup.
    // the agent sends a new url(ticket) to re-join.
    // clear previous popout session data.
    if (!!value && !!oldValue && value !== oldValue && Config.canPopOut) {
      StorageService.removeItem(KEY_VIDEO_POPOUT, "session");
      this.dispatchCallPoppedOutChange?.(false);
    }
  }
  @Prop({ mutable: true }) $testDevicePermissionsUrl: string;
  @Prop({ mutable: true }) $callState: CallStateEnum;
  @Prop({ mutable: true }) $conversationType: ConversationTypeEnum;
  @Prop({ mutable: true }) $conversationState: ConversationStateEnum;
  @Prop({ mutable: true }) $isPoppedOut: boolean;

  private themeOptions: IThemeOptions;

  constructor() {
    ThemeService.optionsAvailable().subscribe((o) => (this.themeOptions = o));
  }

  dispatchCallPoppedOutChange: typeof dispatchCallPoppedOutChange;

  componentWillLoad() {
    store.mapStateToProps(this, (rootState: RootState) => {
      const {
        conversation: { type },
        call: { ticket, state, isPopedOut, testDevicePermissionsUrl },
      } = rootState;
      const convState = rootState.conversation.state;
      return {
        $ticket: ticket,
        $callState: state,
        $conversationType: type,
        $isPoppedOut: isPopedOut,
        $conversationState: convState,
        $testDevicePermissionsUrl: testDevicePermissionsUrl,
      };
    });
    store.mapDispatchToProps(this, {
      dispatchCallPoppedOutChange,
    });
  }

  // when we have chat messages on and kiosk-mode on, stencil flushes the components
  // and calls the disconnected callback. at that point we stop receiving store events
  // since the video call is always rendered, we do not really need this callback.
  // disconnectedCallback() {
  //   this.storeUnsubscribe();
  // }

  registerIFrame(element: HTMLIFrameElement) {
    Log.info(`registering iFrame ${element}`);
    WindowEventService.setTarget(element);
  }

  get isConversationAudio() {
    return this.$conversationType === ConversationTypeEnum.voiceCall;
  }

  openPopup() {
    try {
      // add params to ticket
      const url = new URL(this.$ticket);
      url.searchParams.append(
        PARAM_CONVERSATION_ORIGIN,
        ConversationOriginEnum.POPUP
      );

      // open popup
      const popup = window.open(
        url.toString(),
        POPOUT_WINDOW_TITLE,
        "height=480,width=640"
      );
      popup.focus;
      WindowEventService.setPopOut(popup);
      this.popOut.emit();

      StorageService.setItem(KEY_VIDEO_POPOUT, "true", "session");
      this.dispatchCallPoppedOutChange(true);

      /* // currently commented out because on pop-in we show the device setup  
      // listen in case it closes and we are still in the same page (not refreshed)
      const timer = setInterval(() => {
        if (popup?.closed) {
          clearInterval(timer);
          this.popIn.emit();
          this.dispatchCallPoppedOutChange(false);
        }
      }, 1000);
      */
    } catch (ex) {
      Log.error(ex);
      AVBusService.errorLog(
        new HandledError(ex, ErrorCode.CONVERSATION_URL_PARSE_FAILED)
      );
    }
  }

  get isVideoCardOpen() {
    return this.open && !this.$isPoppedOut;
  }
  get isFrameAvailable() {
    return (
      this.$testDevicePermissionsUrl ||
      (this.$ticket &&
        (this.isConversationAudio ||
          this.isCallEnding ||
          (!this.isConversationAudio && this.isVideoCardOpen)))
    );
  }
  get isExpanded() {
    return !Config.kioskMode && this.$callState === CallStateEnum.started;
  }
  get isCallEnding() {
    return this.$callState === CallStateEnum.ending;
  }
  get isSecondary() {
    return (
      !Config.kioskMode &&
      this.isConversationAudio &&
      this.$callState !== CallStateEnum.feedback
    );
  }
  get isTestingDevicePermissions() {
    return (
      this.$conversationState === ConversationStateEnum.testingDevicePermissions
    );
  }
  get isFeedback() {
    return this.$callState === CallStateEnum.feedback;
  }
  get isConsent() {
    return this.$callState === CallStateEnum.consent;
  }
  get isStarting() {
    return this.$callState === CallStateEnum.starting;
  }
  get isStarted() {
    return this.$callState === CallStateEnum.started;
  }
  get isPositionLeft() {
    return this.themeOptions.positionHorizontal === "left";
  }
  get isPositionRight() {
    return this.themeOptions.positionHorizontal === "right";
  }
  get isPositionCenter() {
    return this.themeOptions.positionHorizontal === "center";
  }
  get isPositionTop() {
    return this.themeOptions.positionVertical === "top";
  }
  get isPositionBottom() {
    return this.themeOptions.positionVertical === "bottom";
  }
  get canPopOut() {
    return (
      Config.canPopOut &&
      this.isStarted &&
      !DeviceService.isMobile &&
      this.$conversationType !== ConversationTypeEnum.voiceCall
    );
  }
  get iFrameSource() {
    return this.$ticket || this.$testDevicePermissionsUrl;
  }

  render() {
    return (
      <Host>
        <av-card
          resizable={!DeviceService.isMobile && !Config.kioskMode}
          open={this.isVideoCardOpen}
          focused={this.focused}
          class={{
            "av-card-frame": true,
            "av-card-frame-expanded": this.isExpanded,
            "av-card-frame-fullscreen": Config.kioskMode,
            "av-card-frame-mobile": !Config.kioskMode && DeviceService.isMobile,
            "av-card-frame-secondary": this.isSecondary,
            "av-card-frame-left": this.isPositionLeft,
            "av-card-frame-right": this.isPositionRight,
            "av-card-frame-center": this.isPositionCenter,
          }}
        >
          {this.canPopOut && (
            <button
              data-tid="video/pop-out"
              class={{
                "button-popup-open": true,
                "button-popup-ne":
                  this.themeOptions.positionHorizontal === "right",
                "button-popup-nw":
                  this.themeOptions.positionHorizontal !== "right",
              }}
              onClick={this.openPopup.bind(this)}
            >
              <av-asset image="open"></av-asset>
            </button>
          )}
          <div slot="card-body">
            {!this.focused && (
              <div
                class="frame-cover"
                onClick={() => this.focusChange.emit()}
              ></div>
            )}
            {this.isFrameAvailable && (
              <iframe
                data-tid="video-call/iframe"
                name="video-call/iframe"
                ref={this.registerIFrame.bind(this)}
                class="frame"
                allow="camera; microphone; geolocation; fullscreen; display-capture; autoplay;"
                src={this.iFrameSource}
              ></iframe>
            )}
          </div>
        </av-card>
        {this.open && this.$isPoppedOut && !this.isFeedback && (
          <div
            class={{
              info: true,
              "info-left": this.themeOptions.positionHorizontal === "left",
              "info-top": this.themeOptions.positionVertical === "top",
            }}
          >
            <div class="agent-container">
              <av-agent>
                <av-translate>Agent</av-translate>
              </av-agent>
              <av-status />
            </div>
          </div>
        )}
      </Host>
    );
  }
}
